import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "../../../atom/Dropdown/Dropdown";

import styles from "./index.module.css";

import { CheckIcon } from "../../../../icons";
import SpaceDot from "../../../atom/Space/SpaceDot";
import { SpaceType } from "../../../../types/space";
import SpaceAddModal from "../../../../views/TaskDetail/Space/SpaceAddModal";
import { getSpaceColor } from "../../../../services/space/space.service";
import { useRef, useState } from "react";
import clsx from "clsx";
import { ReactComponent as DeleteIcon } from "../../../../assets/TaskDetail/delete-icon.svg";
import { Button, Flex } from "@radix-ui/themes";
import { AddIcon } from "../../../../icons";
import { useRecoilValue } from "recoil";
import { inProgressAndCompletedSpaceListState } from "../../../../recoil/spaces/inProgressSpaceListState";

interface SpaceDropdownProps {
  selectedValue?: string;
  spaceList: SpaceType[];
  onSelect: (value: number) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectDropdownRef: React.RefObject<HTMLDivElement>;
  handleDeleteProject: (event: React.MouseEvent) => void;
  onFocusClose: () => void;
  onKeydownEscape: () => void;
}

const SpaceDropdown = ({
  selectedValue,
  spaceList,
  onSelect,
  open,
  onOpenChange,
  onFocusClose,
  onKeydownEscape,
  projectDropdownRef,
  handleDeleteProject,
}: SpaceDropdownProps) => {
  // 기존에 선택된 space가 archive된 경우 선택된 space 노출은 해주기 위해 추가
  const inProgressAndCompletedSpaceList = useRecoilValue(inProgressAndCompletedSpaceListState);

  const spaceAddButtonRef = useRef<HTMLButtonElement>(null);

  const [isProjectAddModal, setIsProjectAddModal] = useState(false);
  const handleOpenChange = (isOpen: boolean) => {
    onOpenChange(isOpen);
  };

  const handelCloseAddModal = () => {
    setIsProjectAddModal(false);
    spaceAddButtonRef.current?.focus();
  };

  const handleEscapeKeyDown = (e: KeyboardEvent) => {
    // esc 눌렀을 때 전파 막기(해당 dropdown만 닫히도록)
    e.stopPropagation();

    // add modal 열려있을 때 esc 눌렀을 때 모달 닫기
    if (isProjectAddModal) {
      e.preventDefault(); // esc 눌렀을 때 해당 모달만 닫히도록 하기 위해 추가(없으면 전체 닫힘)
      setIsProjectAddModal(false);
      spaceAddButtonRef.current?.focus();
      return;
    }

    onKeydownEscape();
  };

  const handleDropdownItemKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, spaceId: number) => {
    if (e.key === "Enter") {
      e.stopPropagation(); // enter 눌렀을 때 상위 모달 닫히지 않도록 막기
      handleOpenChange(false);
    }
  };

  return (
    <DropdownMenu
      open={open}
      onOpenChange={handleOpenChange}
      modal={false}
    >
      <DropdownMenuTrigger
        className={clsx(styles.trigger, {
          [styles.trigger_focus]: open,
        })}
      >
        <div className={styles.trigger_content}>
          {selectedValue ? (
            <div
              className={styles.selected_space}
              style={{
                backgroundColor:
                  "" + getSpaceColor(inProgressAndCompletedSpaceList, Number(selectedValue)) + "40",
              }}
            >
              <SpaceDot
                color={getSpaceColor(inProgressAndCompletedSpaceList, Number(selectedValue))}
              />
              <span
                className={clsx(styles.selected_space_label, {
                  [styles.label_on]: selectedValue,
                })}
              >
                {
                  inProgressAndCompletedSpaceList.filter(
                    (project) => project.id === Number(selectedValue)
                  )[0]?.title
                }
              </span>

              <div
                className={styles.selected_space_delete}
                onClick={handleDeleteProject}
              >
                <DeleteIcon className={styles.selected_space_delete_icon} />
              </div>
            </div>
          ) : (
            <div className={styles.space_empty_title}>Add a space</div>
          )}
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        ref={projectDropdownRef}
        sideOffset={5}
        align={"start"}
        side={"bottom"}
        onCloseAutoFocus={(e) => {
          e.preventDefault();
          setIsProjectAddModal(false);
          onFocusClose();
        }}
        onEscapeKeyDown={handleEscapeKeyDown}
        className={styles.dropdown_content}
      >
        <DropdownMenuRadioGroup
          value={selectedValue}
          onValueChange={(value) => {
            onSelect(Number(value));
          }}
        >
          {spaceList.map((space) => (
            <DropdownMenuRadioItem
              key={space.id}
              value={space.id.toString()}
              className={styles.dropdown_item}
              onKeyDown={(e) => handleDropdownItemKeyDown(e, Number(space.id))}
            >
              <SpaceDot
                color={space.color}
                className={styles.leftSlot}
              />
              <div className={styles.dropdown_item_content}>{space.title}</div>
              {selectedValue === space.id.toString() && <CheckIcon />}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
        {/* 아래는 space 추가 모달 트리거 */}
        <DropdownMenuItem
          asChild
          className={styles.dropdown_item}
          onSelect={(e) => {
            e.preventDefault();
            setIsProjectAddModal(true);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.stopPropagation();
              setIsProjectAddModal(true);
            }
          }}
        >
          <Button
            ref={spaceAddButtonRef}
            className={styles.space_add_button}
          >
            <Flex
              align={"center"}
              gap={"6px"}
              className={styles.space_add}
            >
              <AddIcon color={"#CACDCE"} />
              <span className={styles.space_add_title}>
                {spaceList.length ? "New Space" : "Create your first space"}
              </span>
            </Flex>
          </Button>
        </DropdownMenuItem>
        {isProjectAddModal && <SpaceAddModal onClose={handelCloseAddModal} />}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default SpaceDropdown;
